import React, { useRef, useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  createReturn, getReturns,
  getReturnableItems, clearReturnableItems,
} from '../../redux/features/sales/saleReturnSlice';
import { getSales } from '../../redux/features/sales/saleSlice';
import { getallAttributes } from '../../redux/features/product/attributeSlice';
import { useTable, usePagination, useGlobalFilter, useFilters } from 'react-table';
import { GlobalFilter } from '../globalFilter';
import { SelectColumnFilter, multiSelectFilter } from './dashBoard/SelectColumnFilter1';
import { ColumnToggle } from './ColumnToggle';
import { useRedirectLogOutUser } from "../../customHook/useRedirectLogOutUser";
import { useRedirectEmployee } from "../../customHook/useRedirectEmploye";
import { Modal } from "bootstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import ButtonLoading from '../../loader/ButtonLoader';
import { AiOutlinePlus } from "react-icons/ai";
import { MdAssignmentReturn } from "react-icons/md";
import { getBoxInfo } from '../../utils/boxInfo';
import { formatEthiopianDate } from '../../utils/ethiopianCalendar';
import { SimpleDualDateInput } from '../DualDateInput';


const dash = (v) => (v == null || v === "") ? "—" : v;

const initialReturnForm = {
  refundMethod: "cash",
  bankName:     "",
  reason:       "",
  note:         "",
  returnDate:   "",
};

export const SaleReturn = () => {
  useRedirectLogOutUser();
  useRedirectEmployee();

  const dispatch = useDispatch();
  const returnModalRef = useRef(null);

  const { returns, returnableItems, isLoading } =
    useSelector(s => s.saleReturn);
  const { sales }      = useSelector(s => s.sale);
  const { attributes } = useSelector(s => s.attribute);
  const { userType }   = useSelector(s => s.auth);
  const isAdmin        = userType === "admin";

  const [returnForm,   setReturnForm]   = useState(initialReturnForm);
  const [returnQtys,   setReturnQtys]   = useState({}); // productId → qty
  const [selectedSale, setSelectedSale] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasFetched = useRef(false);
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    dispatch(getReturns());
    dispatch(getSales());
    dispatch(getallAttributes());
  }, [dispatch]);

  // ── Sale options for selector ────────────────────────────
  const saleOptions = useMemo(() =>
    (Array.isArray(sales) ? sales : [])
      .filter(s => !s.isVoided)
      .map(s => ({
        value: s._id,
        label: [
          `#${s._id.slice(-6).toUpperCase()}`,
          s.customer?.name || s.customerName || "Walk-in",
          formatEthiopianDate(s.createdAt),
          `${s.totalAmount?.toLocaleString()} birr`,
        ].join(" — "),
      })),
  [sales]);

  // ── Bank options from attributes ─────────────────────────
  const bankOptions = useMemo(() =>
    attributes
      .find(a => a.name?.toLowerCase() === "bank")
      ?.options?.map(o => ({ value: o, label: o })) || [],
  [attributes]);

  const refundMethodOptions = [
    { value: "cash",         label: "Cash" },
    { value: "bank",         label: "Bank Transfer" },
    { value: "store_credit", label: "Store Credit" },
  ];

  const selectStyles = {
    control: (b) => ({ ...b, minHeight: "46px", fontSize: "1.5rem" }),
    menu:    (b) => ({ ...b, zIndex: 9999, fontSize: "1.5rem" }),
  };

  // ── Open return modal ─────────────────────────────────────
  const openReturnModal = (sale = null) => {
    dispatch(clearReturnableItems());
    setReturnQtys({});
    setReturnForm(initialReturnForm);

    if (sale) {
      // Pre-load from sales list click
      const opt = saleOptions.find(o => o.value === sale._id);
      setSelectedSale(opt || null);
      dispatch(getReturnableItems(sale._id));
    } else {
      setSelectedSale(null);
    }
    document.activeElement?.blur();
    new Modal(returnModalRef.current).show();
  };

  // ── When sale selected in modal ───────────────────────────
  const handleSaleSelect = (opt) => {
    setSelectedSale(opt);
    setReturnQtys({});
    if (opt?.value) {
      dispatch(getReturnableItems(opt.value));
    } else {
      dispatch(clearReturnableItems());
    }
  };

  // ── Computed: total refund for entered quantities ─────────
  const { totalReturnAmount, totalRefund } = useMemo(() => {
    if (!returnableItems) return { totalReturnAmount: 0, totalRefund: 0 };

    let total = 0;
    returnableItems.returnableItems.forEach(item => {
      const qty = Number(returnQtys[String(item.product._id)] || 0);
      total += qty * item.sellingPrice;
    });

    const sale = returnableItems.sale;
    const proportion = sale.totalAmount > 0 ? total / sale.totalAmount : 0;
    const refund     = +(sale.paidAmount * proportion).toFixed(2);

    return { totalReturnAmount: total, totalRefund: refund };
  }, [returnQtys, returnableItems]);

  // ── Submit ────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSale) return toast.error("Please select a sale");
    if (!returnForm.reason.trim()) return toast.error("Please enter a reason");

    const itemsToReturn = returnableItems?.returnableItems
      .filter(item => {
        const qty = Number(returnQtys[String(item.product._id)] || 0);
        return qty > 0;
      })
      .map(item => ({
        product:      item.product._id,
        productBatch: item.productBatch._id,
        quantity:     Number(returnQtys[String(item.product._id)]),
      }));

    if (!itemsToReturn?.length) {
      return toast.error("Please enter at least one return quantity");
    }

    setIsSubmitting(true);
    const result = await dispatch(createReturn({
      saleId:       selectedSale.value,
      items:        itemsToReturn,
      refundMethod: returnForm.refundMethod,
      bankName:     returnForm.bankName || null,
      reason:       returnForm.reason,
      note:         returnForm.note    || null,
      returnDate:   returnForm.returnDate || null,
    }));
    setIsSubmitting(false);

    if (!result.error) {
      dispatch(getSales());
      dispatch(getReturns());
      const inst = Modal.getInstance(returnModalRef.current);
      if (inst) inst.hide();
    }
  };

  // ── Table columns ─────────────────────────────────────────
  const columns = useMemo(() => [
    { Header: 'No', id: 'no', Cell: ({ row }) => row.index + 1 },
    {
      Header: 'Return Date',
      accessor: 'returnDate',
      Filter: SelectColumnFilter, filter: 'multiSelectFilter',
      Cell: ({ value }) => formatEthiopianDate(value),
    },
    {
      Header: 'Sale',
      accessor: 'sale',
      Cell: ({ value }) => (
        <span className="badge bg-primary bg-opacity-25 text-primary
                         rounded-pill px-2 py-1 fs-5">
          #{String(value?._id || value).slice(-6).toUpperCase()}
        </span>
      ),
    },
    {
      Header: 'Customer',
      id: 'customer',
      accessor: row => row.sale,
      Cell: ({ value }) =>
        dash(value?.customer?.name || value?.customerName),
    },
    {
      Header: 'Items Returned',
      accessor: 'items',
      Cell: ({ value }) => (
        <div className="d-flex flex-wrap gap-1">
          {value?.map((item, i) => {
            const unit = item.product?.category?.measurementUnit || "pcs";
            const label = unit === "m2" ? "m²"
              : unit === "linear_meter" ? "m" : "pcs";
            return (
              <span key={i}
                className="badge bg-warning bg-opacity-25 text-warning
                           rounded-pill px-2 py-1 fs-5">
                {item.product?.name || "—"} × {item.quantity} {label}
              </span>
            );
          })}
        </div>
      ),
    },
    {
      Header: 'Refund',
      accessor: 'totalRefundAmount',
      Cell: ({ value }) => (
        <span className="text-danger fw-semibold">
          {value?.toLocaleString()} birr
        </span>
      ),
    },
    {
      Header: 'Method',
      accessor: 'refundMethod',
      Filter: SelectColumnFilter, filter: 'multiSelectFilter',
      Cell: ({ value, row }) => (
        <span>
          <span className="text-capitalize">{value}</span>
          {value === "bank" && row.original.bankName && (
            <span className="badge bg-info bg-opacity-25 text-info
                             ms-1 rounded-pill px-2 py-1 fs-6">
              {row.original.bankName}
            </span>
          )}
        </span>
      ),
    },
    {
      Header: 'Reason',
      accessor: 'reason',
      Cell: ({ value }) => dash(value),
    },
    {
      Header: 'Returned By',
      accessor: 'returnedBy.firstname',
      Filter: SelectColumnFilter, filter: 'multiSelectFilter',
      Cell: ({ value }) => dash(value),
    },
    {
      Header: 'Original Sale Date',
      id: 'saleDate',
      accessor: row => row.sale?.createdAt,
      Cell: ({ value }) => formatEthiopianDate(value),
    },
  ], []);

  const data        = useMemo(() =>
    Array.isArray(returns) ? returns : [], [returns]);
  const filterTypes = useMemo(() => ({ multiSelectFilter }), []);

  const tableInstance = useTable({
    columns, data, filterTypes,
    defaultColumn: { Filter: () => null },
    initialState: { hiddenColumns: ['reason'] },
  }, useFilters, useGlobalFilter, usePagination);

  const {
    getTableProps, getTableBodyProps, headerGroups, page,
    nextPage, previousPage, canNextPage, canPreviousPage,
    gotoPage, pageCount, setGlobalFilter, pageOptions,
    setPageSize, state, prepareRow,
    allColumns, getToggleHideAllColumnsProps,
  } = tableInstance;

  const { pageIndex, globalFilter, pageSize } = state;

  return (
    <div>

      {/* ── RETURN MODAL ──────────────────────────────────────── */}
      <div className="modal fade  modal-dialog-scrollable" ref={returnModalRef} tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered modal-xl">
          <div className="modal-content border-0 rounded-4 shadow-lg p-2">

            <div className="modal-header pb-3">
              <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center justify-content-center
                                rounded-circle"
                  style={{ width: 48, height: 48, backgroundColor: "#fff3cd" }}>
                  <MdAssignmentReturn color="#856404" size={28} />
                </div>
                <div>
                  <h3 className="modal-title fw-semibold mb-1">
                    Process Return
                  </h3>
                  <small className="text-muted fs-5">
                    Select a sale, enter return quantities, confirm refund.
                  </small>
                </div>
              </div>
              <button className="btn-close ms-auto" data-bs-dismiss="modal" />
            </div>

            <form onSubmit={handleSubmit} className="border-0">
              <div className="modal-body py-3">
                <div className="bg-light rounded-3 px-3 py-3">
                  <div className="row g-3">

                    {/* Sale selector */}
                    <div className="col-12">
                      <label className="fs-4">
                        Select Sale <span className="text-danger">*</span>
                      </label>
                      <Select
                        options={saleOptions}
                        value={selectedSale}
                        onChange={handleSaleSelect}
                        placeholder="Search by customer name, date or sale ID..."
                        isClearable isSearchable
                        styles={selectStyles}
                      />
                    </div>

                    {/* Sale summary */}
                    {returnableItems?.sale && (
                      <div className="col-12">
                        <div className="d-flex flex-wrap gap-3 p-3
                                        bg-white border rounded-3">
                          <div>
                            <p className="text-muted mb-0 fs-5">Sale Total</p>
                            <p className="fw-bold mb-0 fs-4">
                              {returnableItems.sale.totalAmount
                                ?.toLocaleString()} birr
                            </p>
                          </div>
                          <div>
                            <p className="text-muted mb-0 fs-5">Paid</p>
                            <p className="fw-bold mb-0 fs-4 text-success">
                              {returnableItems.sale.paidAmount
                                ?.toLocaleString()} birr
                            </p>
                          </div>
                          {returnableItems.sale.unpaidAmount > 0 && (
                            <div>
                              <p className="text-muted mb-0 fs-5">Unpaid</p>
                              <p className="fw-bold mb-0 fs-4 text-danger">
                                {returnableItems.sale.unpaidAmount
                                  ?.toLocaleString()} birr
                              </p>
                            </div>
                          )}
                          <div>
                            <p className="text-muted mb-0 fs-5">Sale Date</p>
                            <p className="fw-bold mb-0 fs-4">
                              {formatEthiopianDate(
                                returnableItems.sale.createdAt
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Return items */}
                    {isLoading && (
                      <div className="col-12 text-center py-3">
                        <div className="spinner-border text-primary" />
                      </div>
                    )}

                    {returnableItems?.returnableItems?.length > 0 && (
                      <div className="col-12">
                        <p className="fw-semibold fs-4 border-bottom pb-2 mb-3">
                          Items — enter quantity to return
                        </p>
                        <div className="d-flex flex-column gap-3">
                          {returnableItems.returnableItems.map((item, i) => {
                            const unit = item.product?.category?.measurementUnit || "pcssss";
                            console.log("items", item);
                            const unitLabel = unit === "m2" ? "m²"
                              : unit === "linear_meter" ? "m" : "pcs";
                            const key = String(item.product._id);
                            const qty = returnQtys[key] || "";
                            const refundForItem = (Number(qty) || 0)
                              * item.sellingPrice;

                            const boxInfo = item.product?.itemsPerBox && qty
                              ? getBoxInfo(Number(qty), item.product)
                              : null;

                            return (
                              <div key={i}
                                className={`border rounded-3 px-3 py-3 bg-white
                                  ${!item.canReturn ? "opacity-50" : ""}`}>
                                <div className="row g-2 align-items-center">

                                  {/* Product info */}
                                  <div className="col-12 col-md-4">
                                    <p className="fw-semibold mb-0 fs-4">
                                      {item.product?.name || "—"}
                                    </p>
                                    <small className="text-muted fs-5">
                                      {item.product?.sku} ·{" "}
                                      Sold: {item.originalQuantity} {unitLabel} ·{" "}
                                      Price: {item.sellingPrice?.toLocaleString()} /
                                      {unitLabel}
                                    </small>
                                    {item.alreadyReturned > 0 && (
                                      <div>
                                        <small className="text-warning fs-5">
                                          Already returned:{" "}
                                          {item.alreadyReturned} {unitLabel}
                                        </small>
                                      </div>
                                    )}
                                  </div>

                                  {/* Max returnable */}
                                  <div className="col-6 col-md-2 text-center">
                                    <p className="text-muted mb-0 fs-5">
                                      Max Returnable
                                    </p>
                                    <p className={`fw-bold mb-0 fs-4 ${
                                      item.canReturn
                                        ? "text-success" : "text-danger"
                                    }`}>
                                      {item.maxReturnQuantity} {unitLabel}
                                    </p>
                                  </div>

                                  {/* Qty input */}
                                  <div className="col-6 col-md-2">
                                    <label className="fs-5 text-muted mb-1 d-block">
                                      Return Qty
                                    </label>
                                    <input
                                      className="form-control border-2 fs-4"
                                      type="number"
                                      min="0"
                                      max={item.maxReturnQuantity}
                                      step={unit === "pcs" ? "1" : "0.001"}
                                      placeholder="0"
                                      disabled={!item.canReturn}
                                      value={qty}
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        if (Number(v) >
                                          item.maxReturnQuantity) {
                                          toast.warning(
                                            `Max ${item.maxReturnQuantity}
                                            ${unitLabel}`
                                          );
                                          return;
                                        }
                                        setReturnQtys(prev => ({
                                          ...prev, [key]: v
                                        }));
                                      }}
                                    />
                                  </div>

                                  {/* Box info */}
                                  <div className="col-6 col-md-2">
                                    {boxInfo && (
                                      <div className="d-flex flex-column gap-1">
                                        <span className="badge bg-primary
                                          bg-opacity-25 text-primary
                                          rounded-pill px-2 py-1 fs-6">
                                          {boxInfo.boxLabel}
                                        </span>
                                        <span className="badge bg-info
                                          bg-opacity-25 text-info
                                          rounded-pill px-2 py-1 fs-6">
                                          {boxInfo.sizeLabel}
                                        </span>
                                      </div>
                                    )}
                                  </div>

                                  {/* Item refund */}
                                  <div className="col-6 col-md-2 text-end">
                                    {refundForItem > 0 && (
                                      <div>
                                        <p className="text-muted mb-0 fs-5">
                                          Refund
                                        </p>
                                        <p className="fw-bold mb-0 fs-4
                                                      text-danger">
                                          {refundForItem.toLocaleString()} birr
                                        </p>
                                      </div>
                                    )}
                                  </div>

                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Refund summary */}
                    {totalReturnAmount > 0 && (
                      <div className="col-12">
                        <div className="d-flex flex-wrap gap-3 p-3
                                        bg-white border border-warning
                                        rounded-3">
                          <div>
                            <p className="text-muted mb-0 fs-5">
                              Items Total
                            </p>
                            <p className="fw-bold mb-0 fs-4">
                              {totalReturnAmount.toLocaleString()} birr
                            </p>
                          </div>
                          <div>
                            <p className="text-muted mb-0 fs-5">
                              Actual Refund
                              <span className="badge bg-info ms-1 fs-6">
                                proportional
                              </span>
                            </p>
                            <p className="fw-bold mb-0 fs-4 text-danger">
                              {totalRefund.toLocaleString()} birr
                            </p>
                          </div>
                          {returnableItems?.sale?.unpaidAmount > 0 && (
                            <div>
                              <p className="text-muted mb-0 fs-5">
                                Debt Reduction
                              </p>
                              <p className="fw-bold mb-0 fs-4 text-warning">
                                {(totalReturnAmount - totalRefund)
                                  .toLocaleString()} birr
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Refund method */}
                    <div className="col-12 col-md-4">
                      <label className="fs-4">
                        Refund Method <span className="text-danger">*</span>
                      </label>
                      <Select
                        options={refundMethodOptions}
                        value={refundMethodOptions.find(
                          o => o.value === returnForm.refundMethod
                        )}
                        onChange={(v) => setReturnForm(prev => ({
                          ...prev, refundMethod: v.value
                        }))}
                        styles={selectStyles}
                      />
                    </div>

                    {/* Bank name */}
                    {returnForm.refundMethod === "bank" && (
                      <div className="col-12 col-md-4">
                        <label className="fs-4">Bank Name</label>
                        <Select
                          options={bankOptions}
                          value={returnForm.bankName
                            ? { value: returnForm.bankName,
                                label: returnForm.bankName }
                            : null}
                          onChange={(v) => setReturnForm(prev => ({
                            ...prev, bankName: v?.value || ""
                          }))}
                          placeholder="Select bank..."
                          isClearable
                          styles={selectStyles}
                        />
                      </div>
                    )}

                    

                    {/* Reason */}
                    <div className="col-12 col-md-6">
                      <label className="fs-4">
                        Reason <span className="text-danger">*</span>
                      </label>
                      <input
                        className="form-control form-control-lg border-2 fs-4"
                        placeholder="e.g. Wrong size, defective tile..."
                        value={returnForm.reason}
                        onChange={(e) => setReturnForm(prev => ({
                          ...prev, reason: e.target.value
                        }))}
                      />
                    </div>

                    {/* Return date */}
                    <div className="col-12 col-md-4">
                      <SimpleDualDateInput
                        label="Return Date"
                        value={returnForm.returnDate}
                        onChange={(date) => setReturnForm(prev => ({ ...prev, returnDate: date }))}
                        maxDate={new Date()}
                      />
                    </div>

                    {/* Note */}
                    <div className="col-12 col-md-6">
                      <label className="fs-4">Note</label>
                      <input
                        className="form-control form-control-lg border-2 fs-4"
                        placeholder="Optional additional note..."
                        value={returnForm.note}
                        onChange={(e) => setReturnForm(prev => ({
                          ...prev, note: e.target.value
                        }))}
                      />
                    </div>

                  </div>
                </div>
              </div>

              <div className="modal-footer border-0 pt-0 gap-2">
                <button type="button"
                  className="btn btn-danger border flex-fill rounded-3
                             fw-medium py-2 fs-2"
                  data-bs-dismiss="modal">Cancel</button>
                {isSubmitting ? (
                  <ButtonLoading
                    className="btn btn-warning flex-fill rounded-3
                               fw-medium py-2 fs-2"
                    disabled>
                    Processing...
                  </ButtonLoading>
                ) : (
                  <button type="submit"
                    className="btn btn-warning flex-fill rounded-3
                               fw-medium py-2 fs-2 text-dark">
                    <MdAssignmentReturn className="me-2" size={20} />
                    Confirm Return
                  </button>
                )}
              </div>
            </form>

          </div>
        </div>
      </div>

      {/* ── HEADER ───────────────────────────────────────────── */}
      <div className="d-flex justify-content-between align-items-center
                      m-4 px-4 py-2 rounded-4 border"
        style={{ backgroundColor: "#faf8f5" }}>
        <h4 className="mb-0 fs-2">Sale Returns</h4>
        <button className="btn btn-warning rounded-3 text-dark"
          onClick={() => openReturnModal()}>
          <MdAssignmentReturn className="me-1" />
          Process Return
        </button>
      </div>

      {/* ── SEARCH + TOGGLE ──────────────────────────────────── */}
      <div className="px-4 mb-4">
        <div className="d-flex align-items-center justify-content-between
                        gap-3 px-4 rounded-4 border"
          style={{ backgroundColor: "#faf8f5" }}>
          <GlobalFilter filter={globalFilter} setFilter={setGlobalFilter} />
          <ColumnToggle
            allColumns={allColumns}
            getToggleHideAllColumnsProps={getToggleHideAllColumnsProps}
          />
        </div>
      </div>

      {/* ── TABLE ────────────────────────────────────────────── */}
      <div className="px-4">
        <div className="table-responsive rounded-4 border mt-3"
          style={{ backgroundColor: "#ffffff" }}>
          <table className="table table-hover table-striped fs-4"
            {...getTableProps()}
            style={{ minWidth: "100%", margin: 0 }}>
            <thead>
              {headerGroups.map(hg => (
                <tr {...hg.getHeaderGroupProps()}>
                  {hg.headers.map(col => (
                    <th className="px-4"
                      style={{ backgroundColor: "#faf8f5" }}
                      {...col.getHeaderProps()}>
                      {col.render('Header')}
                      {col.canFilter && col.render('Filter')}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map(row => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map(cell => (
                      <td className="px-4"
                        style={{ backgroundColor: "#ffffff" }}
                        {...cell.getCellProps()}>
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                );
              })}
              {page.length === 0 && (
                <tr>
                  <td colSpan={columns.length}
                    className="text-center text-muted py-5 fs-4">
                    No returns recorded yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── PAGINATION ───────────────────────────────────────── */}
      <div className="mt-3">
        <ul className="pagination justify-content-center pagination-lg">
          <span style={{ padding: "5px", fontSize: "15px" }}>
            page{" "}
            <strong>
              <em style={{ color: "blue" }}>{pageIndex + 1}</em>{" "}
              of {pageOptions.length}
            </strong>
          </span>
          <select className="form-select form-select-lg"
            style={{ maxWidth: "90px" }}
            value={pageSize}
            onChange={e => setPageSize(Number(e.target.value))}>
            {[10, 15, 20, 30, 50].map((s, i) => (
              <option key={i} value={s}>Show {s}</option>
            ))}
          </select>
          <li>
            <button className="page-link"
              onClick={() => gotoPage(0)}
              disabled={!canPreviousPage}>{'<<'}</button>
          </li>
          <li className={`page-item ${!canPreviousPage ? "disabled" : ""}`}>
            <button className="page-link"
              onClick={previousPage}
              disabled={!canPreviousPage}>Previous</button>
          </li>
          <li className={`page-item ${!canNextPage ? "disabled" : ""}`}>
            <button className="page-link"
              onClick={nextPage}
              disabled={!canNextPage}>Next</button>
          </li>
          <li>
            <button className="page-link"
              onClick={() => gotoPage(pageCount - 1)}
              disabled={!canNextPage}>{'>>'}</button>
          </li>
        </ul>
      </div>

    </div>
  );
};